import { z } from "zod";

// 定义基础类型
const FieldTypeSchema = z.enum([
  "trend",
  "i18n",
  "percentage",
  "text",
  "text_series",
  "number",
  "image_series",
  "currency",
  "entity_id",
  "skus",
  "sku_props",
  "image",
  "url",
  "datetime",
]);

export const DatetimeTypeSchema = z.number();
export type DatetimeType = z.infer<typeof DatetimeTypeSchema>;

export const UrlTypeSchema = z.string();
export type UrlType = z.infer<typeof UrlTypeSchema>;

export const TrendTypeSchema = z.array(
  z.object({
    timestamp: z.number(),
    value: z.number(),
  }),
);
export type TrendType = z.infer<typeof TrendTypeSchema>;

export type FieldType = z.infer<typeof FieldTypeSchema>;
// text
export const TextTypeSchema = z.string();
export type TextType = z.infer<typeof TextTypeSchema>;

// boolen
export const BooleanTypeSchema = z.boolean();
export type BooleanType = z.infer<typeof BooleanTypeSchema>;

// text series
export const TextSeriesTypeSchema = z.array(z.string());
export type TextSeriesType = z.infer<typeof TextSeriesTypeSchema>;

// number
export const NumberTypeSchema = z.number();
export type NumberType = z.infer<typeof NumberTypeSchema>;

// percentage
export const PercentageTypeSchema = z.number();
export type PercentageType = z.infer<typeof PercentageTypeSchema>;

// image series
export const ImageSeriesTypeSchema = z.array(
  z.object({ url: z.string(), thumb_url: z.string() }),
);
export type ImageSeriesType = z.infer<typeof ImageSeriesTypeSchema>;

// shopify image series
export const ShopifyImageSeriesTypeSchema = z.array(
  z.object({ original_image_url: z.string(), resized_image_url: z.string() }),
);
export type ShopifyImageSeriesType = z.infer<
  typeof ShopifyImageSeriesTypeSchema
>;

// image
export const ImageTypeSchema = z.object({
  url: z.string(),
  thumb_url: z.string(),
});
export type ImageType = z.infer<typeof ImageTypeSchema>;

// currency
export const CurrencyTypeSchema = z.object({
  // 暂不确定数据侧有哪些货币单位，先写成string
  unit: z.string(),
  value: z.number(),
});
export type CurrencyType = z.infer<typeof CurrencyTypeSchema>;

// entity id
export const EntityIdTypeSchema = z.string();
export type EntityIdType = z.infer<typeof EntityIdTypeSchema>;

// skus
export const SkusTypeSchema = z.array(
  z.object({
    sku_id: z.string(),
    price: z.object({
      discount: z.string(),
      original_price: z.string(),
      original_price_value: z.string(),
      real_price: z.number(),
      unit_price_desc: z.string(),
    }),
    sku_sale_props: z.array(
      z.object({
        prop_id: z.string(),
        prop_name: z.string(),
        prop_value: z.string(),
        prop_value_id: z.string(),
      }),
    ),
    sale_prop_value_ids: z.string(),
    status: z.number(),
    stock: z.number(),
  }),
);
export type SkusType = z.infer<typeof SkusTypeSchema>;

// sku props
export const SkuPropsTypeSchema = z.array(
  z.object({
    has_image: z.boolean(),
    prop_id: z.string(),
    prop_name: z.string(),
    sale_prop_values: z.array(
      z.object({
        image: z.object({
          url: z.string().optional(),
          thumb_url: z.string().optional(),
        }),
        prop_value: z.string(),
        prop_value_id: z.string(),
      }),
    ),
  }),
);
export type SkuPropsType = z.infer<typeof SkuPropsTypeSchema>;

// i18n
export const I18nTypeSchema = z.string();
export type I18nType = z.infer<typeof I18nTypeSchema>;

// collection
export const CollectionTypeSchema = z.array(
  z.object({
    collection_id: z.string(),
    collection_name: z.string(),
    products_count: z.number(),
  }),
);
export type CollectionType = z.infer<typeof CollectionTypeSchema>;

// 访问趋势
export const VisitsListTypeSchema = z.array(
  z.object({
    date: z.string(),
    visits: z.number(),
  }),
);
export type VisitsListType = z.infer<typeof VisitsListTypeSchema>;

// 访问国家来源
export const CountrySourceTypeSchema = z.array(
  z.object({
    country: z.number(),
    country_code: z.string(),
  }),
);
export type CountrySourceType = z.infer<typeof CountrySourceTypeSchema>;

// 流量来源
export const VisitsSourceTypeSchema = z.object({
  direct: z.number().nullish(),
  mail: z.number().nullish(),
  paid_referrals: z.number().nullish(),
  referrals: z.number().nullish(),
  social: z.number().nullish(),
  search: z.number().nullish(),
});
export type VisitsSourceType = z.infer<typeof VisitsSourceTypeSchema>;

// shopify video url
export const ShopifyVideoUrlTypeSchema = z.array(
  z.object({ video_hd_url: z.string(), video_preview_image_url: z.string() }),
);
export type ShopifyVideoUrlType = z.infer<typeof ShopifyVideoUrlTypeSchema>;

// shopify ads card
export const ShopifyAdsCardTypeSchema = z.array(
  z.object({
    body: z.string().nullish(),
    caption: z.string().nullish(),
    cta_text: z.string().nullish(),
    cta_type: z.string().nullish(),
    link_description: z.string().nullish(),
    link_url: z.string().nullish(),
    original_image_url: z.string().nullish(),
    resized_image_url: z.string().nullish(),
    title: z.string().nullish(),
  }),
);
export type ShopifyAdsInternalCardType = z.infer<
  typeof ShopifyAdsCardTypeSchema
>;

// 定义 common render 类型
export const CommonTypeSchema = z.union([
  z.object({
    fieldType: z.literal("text"),
    value: TextTypeSchema,
  }),
  z.object({
    fieldType: z.literal("number"),
    value: NumberTypeSchema.nullable(),
  }),
  z.object({
    fieldType: z.literal("percentage"),
    value: PercentageTypeSchema.nullable(),
  }),
  z.object({
    fieldType: z.literal("image_series"),
    value: ImageSeriesTypeSchema,
  }),
  z.object({
    fieldType: z.literal("currency"),
    value: CurrencyTypeSchema.nullable(),
  }),
  z.object({
    fieldType: z.literal("i18n"),
    value: I18nTypeSchema,
  }),
  z.object({
    fieldType: z.literal("datetime"),
    value: NumberTypeSchema,
  }),
  z.object({
    fieldType: z.literal("entity_id"),
    value: EntityIdTypeSchema,
  }),
  z.object({
    fieldType: z.literal("decimal"),
    value: NumberTypeSchema,
  }),
  z.object({
    fieldType: z.literal("date"),
    value: NumberTypeSchema,
  }),
  z.object({
    fieldType: z.literal("duration"),
    value: NumberTypeSchema,
  }),
]);
export type CommonType = z.infer<typeof CommonTypeSchema>;

// 定义字段配置
export const FieldConfigSchema = z.object({
  fieldKey: z.string(),
  beFieldKey: z.string(),
  subColName: z.string().optional(),
  isSelected: z.boolean().optional(),
});

// 定义 col render type
export const ColRenderTypeSchema = z.union([
  z.enum([
    "common",
    "productInfo",
    "shopInfo",
    "trend",
    "creatorInfo",
    "relatedVideo",
    "relatedLive",
    "videoInfo",
    "liveInfo",
    "liveDurationTime",
    "rank",
    "sellerShopInfo",
    "liveShopInfo",
    "categoryLabelList",
    "foundTime",
    "advertiserInfo",
    "adsInfo",
    "videoProductInfo",
    "shopifyShopInfo",
    "shopifySellerProductInfo",
    "collectionInfo",
    "sellerVisitInfo",
    "visitsTrend",
    "visitsSource",
    "countrySource",
    "shopifyProductInfo",
    "categoryInfo",
    "gmvTrend",
    "soldCountTrend",
    "agencyInfo",
    "categoryLabelRank",
    "group",
    "top3Product",
    "categoryTop3Product",
    "creatorTop3Product",
    "liveTop3Product",
    "top3Video",
    "top3Creator",
  ]),
  z.string(), // 允许任何字符串，用于处理新增的 enum 值
]);

export type ColRenderType = z.infer<typeof ColRenderTypeSchema>;

// 定义列配置
export const ColumnConfigSchema = z.object({
  fieldList: z.array(FieldConfigSchema),
  colNameI18nKey: z.string(),
  colRenderType: ColRenderTypeSchema,
  colID: z.string(),
  size: z.number().optional(),
  groupSize: z.number().optional(),
  allowSort: z.boolean(),
  sortFieldKeys: z.array(z.string()).optional(),
  headerClassName: z.string().optional(),
});

const ActionConfigSchema = z.object({
  actionNameI18nKey: z.string(),
  actionType: z.enum(["detail", "star", "modify-group"]),
});

// 定义操作列配置
export const ActionColumnConfigSchema = z.object({
  actions: z.array(ActionConfigSchema),
  colNameI18nKey: z.string(),
  colID: z.string(),
});

// 定义表格配置
export const TableConfigSchema = z.object({
  meta: z.object({
    tableId: z.string(),
    version: z.string(),
    apiPath: z.string(),
    entityName: z.string(),
    viewName: z.string(),
    resourceKey: z.string(),
    relatedEntityName: z.string().optional(),
  }),
  tableNameI18nKey: z.string(),
  tableCols: z.array(ColumnConfigSchema),
  leftPinnedColumns: z.array(z.string()).optional(),
  actionCols: z.array(ActionColumnConfigSchema),
  entityIDCol: z.string(),
  entityIDFieldKey: z.string(),
  defaultSortConfig: z.array(
    z.object({
      fieldKey: z.string(),
      sortOrder: z.enum(["asc", "desc"]),
    }),
  ),
});

export const BeFieldKeyI18nMapSchema = z.array(
  z.object({
    fieldKey: z.string(),
    i18nKey: z.string(),
    tipsI18nKey: z.string().nullable(),
    tips: z
      .object({
        zh: z.string(),
        en: z.string(),
      })
      .optional(),
  }),
);
export type BeFieldKeyI18nMapType = z.infer<typeof BeFieldKeyI18nMapSchema>;

export type TableConfigType = z.infer<typeof TableConfigSchema>;

export const ShopifySkuTypeSchema = z.array(
  z.object({
    id: z.number(),
    price: z.string(),
    title: z.string(),
    thumb_url: z.string(),
    url: z.string(),
  }),
);
export type ShopifySkuType = z.infer<typeof ShopifySkuTypeSchema>;

export const ShopifySkuPropsTypeSchema = z.array(
  z.object({
    position: z.number(),
    name: z.string(),
    values: z.array(z.string()),
  }),
);
export type ShopifySkuPropsType = z.infer<typeof ShopifySkuPropsTypeSchema>;
